import { jsx as _jsx } from "react/jsx-runtime";
import MuiButton from '@mui/material/Button';
import MuiIconButton from '@mui/material/IconButton';
import * as React from 'react';
import { Badge } from '../badge';
import { getIcon } from '../icon';
import { usePopoverProps } from '../popover';
import { styled } from '../styled';
import { useRubiksTheme } from '../theme';
import { Tooltip } from '../tooltip';
import { makeEnum, withVersion } from '../utils';
const iconButtonSizes = ['smaller', 'small', 'medium', 'large'];
export const IconButtonSize = makeEnum(...iconButtonSizes);
const SIZE_CONFIG = {
    smaller: { button: 2.75, icon: 2 },
    small: { button: 4.25, icon: 3 },
    medium: { button: 5, icon: 3 },
    large: { button: 6, icon: 3 }
};
const variants = ['contained', 'text'];
export const IconButtonVariant = makeEnum(...variants);
const StyledIconButton = styled(MuiIconButton)(({ theme, $size }) => {
    const cfg = SIZE_CONFIG[$size];
    return {
        width: theme.spacing(cfg.button),
        height: theme.spacing(cfg.button),
        padding: 0
    };
});
const StyledContainedButton = styled(MuiButton)(({ theme, $size }) => {
    const cfg = SIZE_CONFIG[$size];
    return {
        width: theme.spacing(cfg.button),
        height: theme.spacing(cfg.button),
        minWidth: 0,
        padding: 0,
        '& [data-role="icon"]': {
            fill: 'currentColor',
            color: 'inherit'
        }
    };
});
const IconButtonImpl = React.forwardRef((props, ref) => {
    const { icon, badge, iconProps: _iconProps, tooltip, tooltipPlacement, size = 'medium', variant = 'text', color, ...muiProps } = props;
    const iconColor = getColorProp(color);
    const Icon = icon ? getIcon(icon) : () => null;
    const cfg = SIZE_CONFIG[size];
    const iconSizeSx = {
        fontSize: (theme) => theme.spacing(cfg.icon)
    };
    const iconProps = {
        ..._iconProps,
        sx: _iconProps?.sx
            ? [
                iconSizeSx,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                ...(Array.isArray(_iconProps.sx)
                    ? _iconProps.sx
                    : [_iconProps.sx])
            ]
            : iconSizeSx
    };
    const content = (_jsx(Badge, { ...badge, children: muiProps.children ?? _jsx(Icon, { color: iconColor, ...iconProps }) }));
    return (_jsx(Tooltip, { bypass: !tooltip, title: tooltip, placement: tooltipPlacement, children: variant === 'contained' ? (_jsx(StyledContainedButton, { ref: ref, "data-role": "icon-button", variant: "contained", "$size": size, disableElevation: true, color: color, ...muiProps, children: content })) : (_jsx(StyledIconButton, { ref: ref, "data-role": "icon-button", "$size": size, color: color, ...muiProps, children: content })) }));
});
export const IconButton = withVersion(IconButtonImpl, '1.2.0');
function getColorProp(color) {
    if (color === 'default') {
        return undefined;
    }
    return color;
}
export function useIconButtonPopoverProps(props) {
    const { ThemeProvider } = useRubiksTheme();
    const { theme, behaviour, ...iconButtonProps } = props;
    return usePopoverProps({
        disabled: props.disabled,
        label: ({ open, ...labelProps }) => {
            const color = getColorProp(iconButtonProps.color);
            const button = (_jsx(IconButton, { ...iconButtonProps, iconProps: {
                    color: color ?? (open ? 'primary' : undefined),
                    ...props.iconProps
                }, ...labelProps }));
            return theme ? (_jsx(ThemeProvider, { theme: theme, children: button })) : (button);
        },
        behaviour,
        'aria-label': props['aria-label']
    });
}
